// Calendar List View Component (VAC-8)
// Chronological list of all vacation periods

import React, { useMemo } from 'react'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { Calendar } from 'lucide-react'
import type { CalendarEntry } from '@/types/vacation'

interface CalendarListViewProps {
  vacationEntries: CalendarEntry[]
  isLoading?: boolean
}

export const CalendarListView: React.FC<CalendarListViewProps> = ({
  vacationEntries,
  isLoading = false
}) => {
  // Sort entries by start date (chronological order)
  const sortedEntries = useMemo(() => {
    return [...vacationEntries].sort((a, b) => {
      return new Date(a.startDate).getTime() - new Date(b.startDate).getTime()
    })
  }, [vacationEntries])

  // Group entries by month
  const entriesByMonth = useMemo(() => {
    const groups = new Map<string, CalendarEntry[]>()

    sortedEntries.forEach(entry => {
      const monthKey = format(new Date(entry.startDate), 'MMMM yyyy', { locale: ptBR })
      if (!groups.has(monthKey)) {
        groups.set(monthKey, [])
      }
      groups.get(monthKey)!.push(entry)
    })

    return Array.from(groups.entries())
  }, [sortedEntries])

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <svg className="animate-spin h-8 w-8 text-brand-blue" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      </div>
    )
  }

  if (entriesByMonth.length === 0) {
    return (
      <div className="text-center py-12">
        <Calendar className="w-16 h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
          Nenhuma férias encontrada
        </h3>
        <p className="text-gray-600 dark:text-gray-400">
          Não há férias cadastradas para o período selecionado.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-8">
      {entriesByMonth.map(([monthKey, entries]) => (
        <div key={monthKey}>
          {/* Month Header */}
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 capitalize border-b border-gray-200 dark:border-gray-700 pb-2">
            {monthKey}
          </h3>

          {/* Entries List */}
          <div className="space-y-3">
            {entries.map((entry) => (
              <div
                key={entry.requestId}
                className="flex items-center justify-between p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-4 flex-1">
                  {/* Employee Avatar */}
                  {entry.employeeAvatar ? (
                    <img
                      src={entry.employeeAvatar}
                      alt={entry.employeeName}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-brand-primary flex items-center justify-center text-brand-dark font-semibold">
                      {entry.employeeName.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                    </div>
                  )}

                  {/* Employee Info */}
                  <div className="flex-1">
                    <p className="font-semibold text-gray-900 dark:text-white">
                      {entry.employeeName}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {format(new Date(entry.startDate), "dd 'de' MMM", { locale: ptBR })} - {format(new Date(entry.endDate), "dd 'de' MMM", { locale: ptBR })}
                    </p>
                  </div>

                  {/* Vacation Details */}
                  <div className="hidden md:flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Duração</p>
                      <p className="font-semibold text-gray-900 dark:text-white">
                        {entry.totalDays} {entry.totalDays === 1 ? 'dia' : 'dias'}
                      </p>
                    </div>

                    <div className="text-right">
                      <p className="text-sm text-gray-600 dark:text-gray-400">Tipo</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-white">
                        {entry.type === 'FULL'
                          ? 'Completas'
                          : entry.type === 'SPLIT'
                          ? 'Fracionadas'
                          : 'Venda 1/3'
                        }
                      </p>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <span
                    className={`px-3 py-1 text-xs font-medium rounded-full whitespace-nowrap ${
                      entry.status === 'APPROVED'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300'
                    }`}
                  >
                    {entry.status === 'APPROVED' ? 'Aprovada' : 'Pendente'}
                  </span>
                </div>

                {/* Mobile Details */}
                <div className="md:hidden mt-2 pt-2 border-t border-gray-100 dark:border-gray-700 flex items-center gap-4 text-sm">
                  <span className="text-gray-600 dark:text-gray-400">
                    {entry.totalDays} {entry.totalDays === 1 ? 'dia' : 'dias'}
                  </span>
                  <span className="text-gray-600 dark:text-gray-400">
                    {entry.type === 'FULL' ? 'Completas' : entry.type === 'SPLIT' ? 'Fracionadas' : 'Venda 1/3'}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Summary */}
      <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg">
        <p className="text-sm text-blue-800 dark:text-blue-200">
          <span className="font-semibold">{sortedEntries.length}</span> {sortedEntries.length === 1 ? 'férias' : 'férias'} no total
        </p>
      </div>
    </div>
  )
}
